import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Activity, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Round 95s (Jun 2026) — Yahoo Touch Counter
// -------------------------------------------
// Now that every cron is Massive-primary, healthy state is ~zero
// fresh Yahoo writes. This pill surfaces stock_cache documents that
// were ACTUALLY written-with-Yahoo in the last 24h / 7d / 30d,
// using the per-write `yahoo_last_touched_at` timestamp stamped by
// `_safe_update_stock_cache` — independent of the legacy
// `data_source` field which carries stamps from years-old writes.
//
// Tone shifts from emerald (zero last 7d — perfect Massive coverage)
// to amber (1-25) to rose (26+) so admin gets an at-a-glance signal.

export default function AdminYahooTouchPill() {
  // Round P111u — self-gate; hides even if wrapper is bypassed.
  const { user: _authUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(false);
      try {
        const { data } = await axios.get(
          `${API}/admin/yahoo-touch-stats`,
          { withCredentials: true },
        );
        if (!cancelled) setStats(data);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => { load(); }, [load]);

  if (error) {
    return (
      <div className="text-[10px] text-rose-500 px-2 py-1" data-testid="yahoo-touch-pill-error">
        Yahoo touch stats unavailable
      </div>
    );
  }
  if (loading && !stats) {
    return <div className="h-9 rounded-md shimmer" data-testid="yahoo-touch-pill-loading" />;
  }
  if (!stats) return null;

  const last7d = stats.last_7d || 0;
  const last24h = stats.last_24h || 0;
  const last30d = stats.last_30d || 0;
  const cached = stats.total_cached || 1;
  const legacy = stats.legacy_yahoo_stamped || 0;

  // Tone bucket on the headline 7d number.
  let tone;
  if (last7d === 0) {
    tone = { border: 'border-emerald-400/60 dark:border-emerald-700/50',
             bg: 'bg-emerald-50/70 dark:bg-emerald-950/20',
             icon: 'text-emerald-600 dark:text-emerald-400',
             text: 'text-emerald-700 dark:text-emerald-300' };
  } else if (last7d <= 25) {
    tone = { border: 'border-amber-400/60 dark:border-amber-700/50',
             bg: 'bg-amber-50/70 dark:bg-amber-950/20',
             icon: 'text-amber-600 dark:text-amber-400',
             text: 'text-amber-700 dark:text-amber-300' };
  } else {
    tone = { border: 'border-rose-400/60 dark:border-rose-700/50',
             bg: 'bg-rose-50/70 dark:bg-rose-950/20',
             icon: 'text-rose-600 dark:text-rose-400',
             text: 'text-rose-700 dark:text-rose-300' };
  }

  const sampleCount = (stats.samples || []).length;

  // Round P111u — self-gate (post-hooks; safe with Rules of Hooks).
  if (_authUser?.role !== 'admin') return null;

  return (
    <div
      className={`rounded-md border ${tone.border} ${tone.bg} px-2.5 py-1.5 text-[11px]`}
      data-testid="yahoo-touch-pill"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <Activity className={`w-3.5 h-3.5 shrink-0 ${tone.icon}`} />
        <span className={`font-semibold ${tone.text}`}>Yahoo touches</span>
        <span className="text-muted-foreground/80">last 7d:</span>
        <span className="font-mono font-bold tabular-nums" data-testid="yahoo-touch-pill-7d">
          {last7d.toLocaleString()}
        </span>
        <span className="text-muted-foreground/60">·</span>
        <span className="text-muted-foreground">
          24h <span className="font-mono">{last24h.toLocaleString()}</span> ·
          30d <span className="font-mono">{last30d.toLocaleString()}</span>
        </span>
        <span className="text-muted-foreground/60">·</span>
        <span className="text-muted-foreground" title="Total stock_cache documents">
          / {cached.toLocaleString()} cached
        </span>
        <button
          type="button"
          onClick={load}
          className="ml-auto inline-flex items-center justify-center w-5 h-5 rounded hover:bg-foreground/10 transition"
          title="Refresh stats"
          data-testid="yahoo-touch-pill-refresh"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
        </button>
        {sampleCount > 0 && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="inline-flex items-center gap-0.5 text-[10px] font-semibold hover:underline"
            data-testid="yahoo-touch-pill-toggle"
          >
            {expanded ? (
              <><ChevronUp className="w-3 h-3" /> hide tickers</>
            ) : (
              <><ChevronDown className="w-3 h-3" /> show tickers</>
            )}
          </button>
        )}
      </div>

      {last7d === 0 && (
        <div className="text-[10px] text-muted-foreground mt-1 italic">
          No Yahoo writes in the last 7 days. Yahoo is a dormant
          safety net — Massive is carrying every ticker.
          {legacy > 0 && (
            <> Legacy <span className="font-mono">{legacy.toLocaleString()}</span> rows
            still carry an old <code className="font-mono">data_source</code> stamp
            from pre-Massive writes; this is cosmetic and clears on
            their next refresh.</>
          )}
        </div>
      )}

      {expanded && sampleCount > 0 && (
        <div className="mt-2 pt-2 border-t border-border/40" data-testid="yahoo-touch-pill-samples">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
            Last {sampleCount} Yahoo-touched tickers
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(stats.samples || []).map((s) => (
              <span
                key={`${s.ticker}-${s.last_touched_at}`}
                className="inline-flex items-center gap-1 rounded-sm border border-border/60 bg-background/70 px-1.5 py-0.5 text-[10px] font-mono"
                title={`${s.name || ''}\nsource=${s.data_source}\ntouched=${s.last_touched_at}`}
              >
                <span className="font-bold">{s.ticker}</span>
                <span className="text-muted-foreground/70">
                  {s.data_source === 'massive+yahoo' ? 'gap-fill' :
                   s.data_source === 'yahoo_fallback' ? 'fallback' : 'yahoo'}
                </span>
              </span>
            ))}
          </div>
          <div className="text-[10px] text-muted-foreground mt-1.5">
            If a ticker repeatedly appears here across multiple
            refreshes, Massive likely doesn&apos;t cover it — worth a
            support ticket.
          </div>
        </div>
      )}
    </div>
  );
}
